// Parses [SEND_VIDEO|PHOTO|DOCUMENT:path|caption] markers and renders inline media.
// Markers are read only from the results of the tools that emit them
// (`MEDIA_TOOL_NAMES`, ToolCallDetails) — never from the agent's prose or other
// tools' output, where a quoted marker is just text.
// • Videos   — inline <video controls> player
// • Images   — inline thumbnail, click → lightbox full-size
// • Documents — type-aware icon + caption, click → lightbox (preview or info card)
//
// Media URLs resolve through useApiFileUrl so token clients (relay / phone
// PWA) get an authenticated blob URL — native elements can't carry a Bearer
// header (MA-054). Loopback desktop keeps the direct URL.
import { AlertTriangle, Loader2 } from 'lucide-react';
import { mediaApiPath, useApiFileUrl } from '@/lib/useApiFileUrl';
import { getFileTypeStyle, mimeFromPath } from '@/lib/fileTypeIcon';
import { lightboxStore } from '@/lib/lightboxStore';

export interface MediaMarkerItem {
  kind: 'video' | 'image' | 'document';
  path: string;
  caption?: string | undefined;
}

// eslint-disable-next-line security/detect-unsafe-regex -- bounded capture groups on finite input
const MEDIA_MARKER_RE = /\[SEND_(VIDEO|PHOTO|DOCUMENT):([^|\]\s][^|\]]*?)(?:\|([^\]]*))?\]/g;

export function parseMediaMarkers(text: string): {
  cleaned: string;
  markers: MediaMarkerItem[];
} {
  const markers: MediaMarkerItem[] = [];
  const cleaned = text
    .replace(MEDIA_MARKER_RE, (_match, type: string, rawPath: string, caption?: string) => {
      markers.push({
        kind: type === 'VIDEO' ? 'video' : type === 'PHOTO' ? 'image' : 'document',
        path: rawPath.trim(),
        caption: caption?.trim() || undefined,
      });
      return '';
    })
    .trim();
  return { cleaned, markers };
}

/** Compact placeholder shown while a token client fetches the media bytes. */
function MediaLoading({ caption }: { caption?: string | undefined }) {
  return (
    <div className="mt-2 flex items-center gap-2 rounded-md border border-border bg-background/60 px-3 py-2 text-xs text-muted-foreground">
      <Loader2 className="h-3.5 w-3.5 animate-spin" />
      <span className="truncate">{caption ?? 'Loading media…'}</span>
    </div>
  );
}

/** Failure chip — auth rejection, missing file, or path outside the served roots. */
function MediaError({ filename, error }: { filename: string; error: string }) {
  return (
    <div className="mt-2 flex items-center gap-2 rounded-md border border-destructive/30 bg-destructive/10 px-3 py-2 text-xs text-destructive">
      <AlertTriangle className="h-3.5 w-3.5 shrink-0" />
      <span className="min-w-0 truncate">
        {filename} — {error}
      </span>
    </div>
  );
}

export function MediaEmbed({ marker }: { marker: MediaMarkerItem }) {
  const { url, error } = useApiFileUrl(mediaApiPath(marker.path));
  const filename = marker.path.split('/').pop() ?? 'file';
  const mime = marker.kind === 'video' ? 'video/mp4' : marker.kind === 'image' ? 'image/*' : mimeFromPath(marker.path);
  const { Icon, color, bg } = getFileTypeStyle(mime);

  if (error) return <MediaError filename={filename} error={error} />;
  if (!url) return <MediaLoading caption={marker.caption} />;

  if (marker.kind === 'video') {
    return (
      <div className="mt-2 flex flex-col gap-1">
        {marker.caption && <p className="text-xs text-muted-foreground">{marker.caption}</p>}
        <video controls src={url} className="max-h-[360px] max-w-full rounded-md border border-border bg-black" />
        <a
          href={url}
          download={filename}
          className="self-start text-xs text-primary/70 underline underline-offset-2 hover:text-primary"
        >
          {filename}
        </a>
      </div>
    );
  }

  if (marker.kind === 'image') {
    return (
      <div className="mt-2 flex flex-col gap-1">
        {marker.caption && <p className="text-xs text-muted-foreground">{marker.caption}</p>}
        <button
          type="button"
          className="inline-block focus:outline-none focus-visible:ring-2 focus-visible:ring-primary"
          onClick={() =>
            lightboxStore.open({ url, fileName: marker.caption ?? filename, mimeType: 'image/*', downloadUrl: url })
          }
          title="Click to enlarge"
        >
          <img
            src={url}
            alt={marker.caption ?? filename}
            loading="lazy"
            className="max-h-[420px] max-w-full rounded-md border border-border transition-opacity hover:opacity-90"
          />
        </button>
      </div>
    );
  }

  // Document — type-aware icon + caption, click → lightbox
  return (
    <div className="mt-2">
      <button
        type="button"
        className={`flex w-full items-center gap-2 rounded-md border border-border ${bg} px-3 py-2 text-xs transition-colors hover:brightness-105 focus:outline-none focus-visible:ring-2 focus-visible:ring-primary`}
        onClick={() =>
          lightboxStore.open({
            url,
            fileName: marker.caption ?? filename,
            mimeType: mime,
            downloadUrl: url,
          })
        }
        title={`Open ${filename}`}
      >
        <Icon className={`h-4 w-4 shrink-0 ${color}`} />
        <span className="min-w-0 flex-1 truncate text-left">{marker.caption ?? filename}</span>
      </button>
    </div>
  );
}
